/**
 * Reports catalog — the single source of truth for the report list.
 *
 * Consumed by ReportsLandingPage (renders the cards) and, going forward,
 * the global search index ("where is the negative balances report?").
 * Add a report here once and it shows up in both places.
 *
 * Routes are relative to `/:tenant/manage/reports/` (see REPORTS_ROUTE_PREFIX).
 * The catalog depends on the tenant's participant label (Athlete/Gymnast/…),
 * so it's exposed as a builder rather than a static const.
 */
import React from "react";
import {
  Sparkles,
  Landmark,
  Users,
  Zap,
  Activity,
  Megaphone,
  ShieldAlert,
} from "lucide-react";

/** Reports links are relative to this, e.g. `${prefix}/balances`. */
export const REPORTS_ROUTE_PREFIX = "manage/reports";

export interface ReportLink {
  label: string;
  path: string; // relative to /:tenant/manage/reports/
  /**
   * The permission the report's route requires (App.tsx). A link is shown
   * only to someone who can open it: the landing page listed every report to
   * everyone, so staff clicked into "Access denied" (2026-10-10).
   */
  permission: string;
  ccStaffOnly?: boolean;
}

export interface ReportCategory {
  title: string;
  icon: React.ReactNode;
  iconColor: string;
  links: ReportLink[];
}

export function buildReportCategories(
  participantSingular: string
): ReportCategory[] {
  return [
    {
      title: "AI Insights",
      icon: <Sparkles className="w-5 h-5" />,
      iconColor: "text-purple-500",
      links: [{ label: "AI Summary", path: "ai-insights", permission: "page_reports_ai_insights" }],
    },
    {
      title: "Financial",
      icon: <Landmark className="w-5 h-5" />,
      iconColor: "text-green-500",
      links: [
        { label: "Revenue", path: "revenue", permission: "page_reports_revenue" },
        { label: "Revenue Projections", path: "revenue-projections", permission: "page_reports_revenue_projections" },
        { label: "Negative Balances", path: "balances", permission: "view_membership_invoices" },
        { label: "Accounts Receivable", path: "aging", permission: "page_reports_aging" },
        { label: "Billing Reconciliation", path: "billing-reconciliation", permission: "page_reports_billing_reconciliation" },
        { label: "Annual Registration Fees", path: "annual-registration-fees", permission: "page_reports_annual_registration_fees" },
        { label: "Top Customers by Spend", path: "top-customers", permission: "page_reports_top_customers" },
        { label: "Payment Methods Breakdown", path: "payment-methods-breakdown", permission: "page_reports_payment_methods" },
        { label: "Stripe Payout Reconciliation", path: "stripe-payout-reconciliation", permission: "page_reports_stripe_payouts" },
        { label: "Year-End Statements", path: "year-end-statements", permission: "page_reports_year_end_statements" },
      ],
    },
    {
      title: "Membership",
      icon: <Users className="w-5 h-5" />,
      iconColor: "text-blue-500",
      links: [
        { label: "Enrollments", path: "enrollments", permission: "page_reports_enrollments" },
        { label: "Churn & Retention", path: "churn", permission: "page_reports_churn" },
        { label: "New Members", path: "new-members", permission: "page_reports_new_members" },
        { label: "Annual Comparison", path: "seasonal-comparison", permission: "page_reports_seasonal_comparison" },
        { label: "Email Verification", path: "email-verification", permission: "page_reports_email_verification", ccStaffOnly: true },
        { label: "Active Account Age", path: "membership-tenure", permission: "page_reports_membership_tenure" },
        { label: "Membership Demographics", path: "membership-demographics", permission: "page_reports_membership_demographics" },
      ],
    },
    {
      title: participantSingular,
      icon: <Zap className="w-5 h-5" />,
      iconColor: "text-amber-500",
      links: [
        { label: `${participantSingular} Demographics`, path: "athlete-demographics", permission: "page_reports_athlete_demographics" },
        { label: `${participantSingular} Birthdays`, path: "birthdays", permission: "page_reports_birthdays" },
      ],
    },
    {
      title: "Operations",
      icon: <Activity className="w-5 h-5" />,
      iconColor: "text-cyan-500",
      links: [
        { label: "Attendance Trends", path: "attendance-trends", permission: "page_reports_attendance_trends" },
        { label: "Program Profitability", path: "program-profitability", permission: "page_reports_program_profitability" },
        { label: "Staff Activity", path: "staff-activity", permission: "page_reports_staff_activity" },
        { label: "Web Analytics", path: "web-analytics", permission: "page_reports_web_analytics" },
      ],
    },
    {
      title: "Marketing",
      icon: <Megaphone className="w-5 h-5" />,
      iconColor: "text-pink-500",
      links: [
        { label: "Registration Funnel", path: "registration-funnel", permission: "page_reports_registration_funnel" },
        { label: "Camp & Event Funnel", path: "camp-event-funnel", permission: "page_reports_camp_event_funnel" },
        { label: "Leads by Source", path: "leads-by-source", permission: "page_reports_leads_by_source" },
      ],
    },
    {
      title: "Safety & Compliance",
      icon: <ShieldAlert className="w-5 h-5" />,
      iconColor: "text-red-500",
      links: [{ label: "Incident Reports", path: "incident-reports", permission: "page_reports_incident_reports" }],
    },
  ];
}

/**
 * The categories as one person may see them: only reports they can open (the
 * route's permission; an admin opens everything, as on the route itself), and
 * platform-staff-only reports only for platform staff. Empty categories drop.
 */
export function visibleReportCategories(
  categories: ReportCategory[],
  {
    isAdmin,
    permissions,
    isCCStaff,
  }: { isAdmin: boolean; permissions: Array<{ name: string }> | null | undefined; isCCStaff: boolean }
): ReportCategory[] {
  const can = (name: string) => isAdmin || (permissions || []).some((p) => p?.name === name);
  return categories
    .map((cat) => ({
      ...cat,
      links: cat.links.filter((l) => (!l.ccStaffOnly || isCCStaff) && can(l.permission)),
    }))
    .filter((cat) => cat.links.length > 0);
}
